import { getAbbreviatedUUID } from '@/modules/common/lib/id';
import {
  ILearningBudgetSetupInTheList,
  LearningBudgetSetupStatus,
} from '@/types/learning-budget';
import { Check } from '@mui/icons-material';
import { Button, Chip, IconButton, Typography } from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import { isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { MRT_ColumnDef } from 'material-react-table';

export const getLearningBudgetSetupsTableColumns = (
  onSetupActivate: (data: ILearningBudgetSetupInTheList) => Promise<void>,
): MRT_ColumnDef<ILearningBudgetSetupInTheList>[] => [
  {
    id: 'id',
    header: 'ID#',
    accessorKey: '_id',
    muiTableHeadCellProps: {
      className: 'pl-0',
    },
    Cell: ({ row: { original: setup, toggleExpanded, getIsExpanded } }) => {
      return (
        <Typography
          color="secondary"
          onClick={() => toggleExpanded()}
          className="cursor-pointer inline-flex items-center gap-1"
        >
          <IconButton size="small" color="inherit">
            <ArrowDropDownIcon
              className={`${getIsExpanded() ? 'rotate-180' : ''}`}
            />
          </IconButton>
          <Typography
            component="span"
            color="inherit"
            className="uppercase  font-semibold"
          >
            {getAbbreviatedUUID(setup._id)}
          </Typography>
        </Typography>
      );
    },
  },
  {
    id: 'startDate',
    header: 'Start Date (UTC)',
    accessorKey: 'startDate',
    Cell: ({ row: { original: setup } }) => {
      const date = new Date(setup.startDate);
      if (!isValid(date)) {
        return <Typography>-</Typography>;
      }

      return <Typography>{formatInTimeZone(date, 'utc', 'P')}</Typography>;
    },
  },
  {
    id: 'endDate',
    header: 'End Date (UTC)',
    accessorKey: 'endDate',
    Cell: ({ row: { original: setup } }) => {
      const date = new Date(setup.endDate);
      if (!isValid(date)) {
        return <Typography>-</Typography>;
      }

      return <Typography>{formatInTimeZone(date, 'utc', 'P')}</Typography>;
    },
  },
  {
    id: 'periodType',
    header: 'Period Type',
    accessorKey: 'periodType',
    Cell: ({ row: { original: setup } }) => (
      <Typography variant="body2" className="capitalize">
        {setup.periodType}
      </Typography>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    Cell: ({ row: { original: setup } }) => (
      <Chip
        label={setup.status}
        className={`capitalize rounded-sm text-primary ${
          setup.status === LearningBudgetSetupStatus.Draft
            ? 'bg-gray'
            : setup.status === LearningBudgetSetupStatus.Expired
              ? 'bg-mydra-light-orange'
              : setup.status === LearningBudgetSetupStatus.Active
                ? 'bg-success-light'
                : setup.status === LearningBudgetSetupStatus.Inactive
                  ? 'bg-error-light'
                  : setup.status === LearningBudgetSetupStatus.Scheduled
                    ? 'bg-tag-blue'
                    : 'bg-gray'
        }`}
        size="small"
      />
    ),
  },
  {
    id: 'actions',
    header: 'Actions',
    muiTableHeadCellProps: {
      align: 'right',
      className: 'pr-0',
    },
    muiTableBodyCellProps: {
      align: 'right',
      className: 'pr-0',
    },
    Cell: ({ row: { original: setup } }) => {
      if (setup.status === LearningBudgetSetupStatus.Draft) {
        return (
          <Button
            variant="contained"
            color="primary"
            size="small"
            endIcon={<Check />}
            onClick={() => onSetupActivate(setup)}
          >
            Start
          </Button>
        );
      }

      if (
        setup.status === LearningBudgetSetupStatus.Active ||
        setup.status === LearningBudgetSetupStatus.Scheduled
      ) {
        return (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            disabled
            endIcon={<Check />}
            sx={({ palette }) => ({
              color: `${palette.text.secondary} !important`,
            })}
          >
            {setup.status === LearningBudgetSetupStatus.Active
              ? 'Active'
              : 'Scheduled'}
          </Button>
        );
      }
      return null;
    },
  },
];
